import { useEffect, useState } from "react"

import { getFinancialTransactionItem } from "../../Project/api/project-finance.services.ts"
import { getInvoicePayments } from "../api/invoices.services.ts"
import { FINANCE_ITEM_STATUSES } from "../../Project/ProjectViewPage/constants/finance-item-constants.ts"

export interface InvoicePayment {
  id: string
  value: string
  status?: number
  status_display?: string
  payment_method_display?: string
  payment_date?: string | null
  lineName?: string
}

interface PaymentsState {
  loading: boolean
  /** Completed payments only: the ones `paid_to_date` adds up. */
  payments: InvoicePayment[]
  /** Booked against the invoice but not paid yet (planned, pending, partial). */
  scheduled: InvoicePayment[]
  /** At least one linked line could not be read, so the list may be short. */
  incomplete: boolean
}

interface PaymentSource {
  id: string
  lines?: { finance_line: string; finance_line_name?: string }[]
}

const IDLE: PaymentsState = {
  loading: false,
  payments: [],
  scheduled: [],
  incomplete: false
}

const byPaymentDate = (a: InvoicePayment, b: InvoicePayment) =>
  (a.payment_date ?? "").localeCompare(b.payment_date ?? "")

/** Completed ones are paid; cancelled ones are dropped; the rest are
 * scheduled. One row per payment id. */
const groupByStatus = (
  rows: InvoicePayment[]
): Pick<PaymentsState, "payments" | "scheduled"> => {
  const unique = [...new Map(rows.map((row) => [row.id, row])).values()]
  const payments: InvoicePayment[] = []
  const scheduled: InvoicePayment[] = []
  unique.forEach((payment) => {
    if (payment.status === FINANCE_ITEM_STATUSES.CANCELLED) return
    ;(payment.status === FINANCE_ITEM_STATUSES.COMPLETED ? payments : scheduled).push(
      payment
    )
  })
  return { payments: payments.sort(byPaymentDate), scheduled: scheduled.sort(byPaymentDate) }
}

/** Every payment linked to the invoice, whichever line it is booked on.
 * Null when the server has no such endpoint (an older backend) or refused it. */
const readLinkedPayments = async (
  invoiceId: string
): Promise<InvoicePayment[] | null> => {
  try {
    const response = await getInvoicePayments(invoiceId)
    if (!response?.ok || !Array.isArray(response.data)) return null
    return response.data.map(({ finance_line_name, ...payment }) => ({
      ...payment,
      lineName: finance_line_name
    }))
  } catch {
    return null
  }
}

/** The payments on the lines the invoice bills: misses a payment linked to
 * the invoice from a line it does not bill. */
const readBilledLinePayments = async (
  invoiceId: string,
  lineIds: string[]
): Promise<{ rows: InvoicePayment[]; incomplete: boolean }> => {
  const results = await Promise.allSettled(
    lineIds.map((id) => getFinancialTransactionItem(id))
  )
  const rows: InvoicePayment[] = []
  let incomplete = false
  results.forEach((result) => {
    if (result.status !== "fulfilled" || !result.value?.ok) {
      incomplete = true
      return
    }
    const line = result.value.data
    ;(line?.partial_finances ?? [])
      .filter((payment) => payment.invoice === invoiceId)
      .forEach((payment) => rows.push({ ...payment, lineName: line?.name }))
  })
  return { rows, incomplete }
}

/**
 * The payments booked against an invoice.
 *
 * The invoice payload carries `paid_to_date` but not the payments behind it.
 * The server lists every payment linked to the invoice; a backend without
 * that list falls back to the payments on the lines the invoice bills.
 * The backend counts only completed payments into `paid_to_date`, so only those
 * are listed as paid; cancelled ones are dropped.
 */
export const useInvoicePayments = (
  invoice: PaymentSource | null
): PaymentsState => {
  const [state, setState] = useState<PaymentsState>(IDLE)
  const invoiceId = invoice?.id
  const lineKey = [
    ...new Set((invoice?.lines ?? []).map((line) => line.finance_line))
  ].join(",")

  useEffect(() => {
    const lineIds = lineKey ? lineKey.split(",") : []
    if (!invoiceId) {
      setState(IDLE)
      return
    }
    let current = true
    setState({ ...IDLE, loading: true })

    const load = async () => {
      const linked = await readLinkedPayments(invoiceId)
      if (linked) return { rows: linked, incomplete: false }
      if (!lineIds.length) return { rows: [], incomplete: false }
      return readBilledLinePayments(invoiceId, lineIds)
    }

    void load().then(({ rows, incomplete }) => {
      if (!current) return
      setState({ loading: false, ...groupByStatus(rows), incomplete })
    })

    return () => {
      current = false
    }
  }, [invoiceId, lineKey])

  return state
}
